import { Component, computed, input } from '@angular/core';
import { ReactiveFormsModule, type FormGroup } from '@angular/forms';
import {
  CrntFormControlHelpText,
  CrntFormControlLabel,
  CrntSelect,
} from '@essent/current-web-angular';
import { TrackValueChangeDirective } from '@analytics/events';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CommonModule } from '@angular/common';

import type { RfaAppointmentControls } from '../../models/rfa-appointment';
import type { RfaComponents } from '../RfaComponents';
import type { RfaValueChangeTrackerOptions } from '../../models/rfa-tracker-options.interface';
import type { RfaFormControl } from '../../../level1';
import { valueChangeOptionsToConfig } from '../../helpers/tracker-options-to-config';
import type { RfaSelectOptions } from '../rfa-select';
import type { Timeslot } from './timeslot.interface';
import { getAriaDescribedBy } from '../../helpers/aria-fn-helpers';
import { RfaErrorsComponent } from '../utils/rfa-errors/rfa-errors.component';

@Component({
  selector: 'rfa-appointment',
  standalone: true,
  templateUrl: './rfa-appointment.component.html',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RfaErrorsComponent,
    CrntFormControlLabel,
    CrntFormControlHelpText,
    CrntSelect,
    TrackValueChangeDirective,
    AutomationIdDirective,
  ],
  styleUrls: [
    '../styles.essent.scss',
    '../styles.ed.scss',
    './rfa-appointment.component.scss',
  ],
})
export class RfaAppointmentComponent {
  public readonly form = input.required<FormGroup<RfaAppointmentControls>>();
  public readonly listOfDays = input<string[] | null>(null);
  public readonly listOfTimes = input<Timeslot[] | null>(null);

  public readonly daysOptions = computed(() => {
    const days = this.listOfDays();
    if (days instanceof Array) {
      const sortedDays = [...days].sort((a, b) => a.localeCompare(b));
      return this.arrayToSelectOptions(this.dayControl.options, sortedDays);
    } else {
      return this.dayControl.options;
    }
  });

  public readonly timesOptions = computed(() => {
    const times = this.listOfTimes();
    return times instanceof Array
      ? this.arrayToSelectOptions(this.timeControl.options, null, times)
      : this.timeControl.options;
  });

  get dayControl() {
    return this.form().controls.day;
  }

  get timeControl() {
    return this.form().controls.time;
  }

  valueChangeTrackConfig(
    control: RfaFormControl<string, typeof RfaComponents.Select>
  ) {
    const valueChangeOptions: RfaValueChangeTrackerOptions =
      control.options.analytics?.valueChangeTracker ?? {};

    return valueChangeOptionsToConfig(
      {
        ...valueChangeOptions,
        value: control.value.toString(),
      },
      {
        fieldName: control.options.label,
        hashValue: false,
      }
    );
  }

  private arrayToSelectOptions(
    prototype: RfaSelectOptions,
    days: string[] | null,
    times: Timeslot[] = []
  ): RfaSelectOptions {
    return {
      ...prototype,
      entries: days
        ? days.map((o) => ({ text: o, value: o }))
        : times.map((o) => ({ text: o.start.substring(0, 5), value: o })),
    };
  }

  protected ariaDescribedBy(
    control: RfaFormControl<string, typeof RfaComponents._Any>
  ) {
    return getAriaDescribedBy(control);
  }
}
